import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { draftTopicPage } from "@/lib/topicPages.functions";

export interface TopicFaq {
  q: string;
  a: string;
}

export interface TopicPage {
  slug: string;
  title: string;
  tag: string;
  explainer: string;
  faqs: TopicFaq[];
  related_slugs: string[];
  is_published: boolean;
  updated_at: string;
}

const mapRow = (row: Record<string, unknown>): TopicPage => ({
  slug: String(row.slug),
  title: String(row.title),
  tag: String(row.tag),
  explainer: String(row.explainer ?? ""),
  faqs: Array.isArray(row.faqs) ? (row.faqs as TopicFaq[]) : [],
  related_slugs: Array.isArray(row.related_slugs) ? (row.related_slugs as string[]) : [],
  is_published: Boolean(row.is_published),
  updated_at: String(row.updated_at ?? ""),
});

export const usePublishedTopicPages = () =>
  useQuery({
    queryKey: ["topic-pages", "published"],
    staleTime: 10 * 60 * 1000,
    queryFn: async (): Promise<TopicPage[]> => {
      const { data, error } = await (supabase as any)
        .from("topic_pages")
        .select("slug,title,tag,explainer,faqs,related_slugs,is_published,updated_at")
        .eq("is_published", true)
        .order("title");
      if (error) throw error;
      return (data || []).map(mapRow);
    },
  });

export const useTopicPage = (slug: string | undefined) =>
  useQuery({
    queryKey: ["topic-pages", "detail", slug],
    enabled: !!slug,
    staleTime: 10 * 60 * 1000,
    queryFn: async (): Promise<TopicPage | null> => {
      const { data, error } = await (supabase as any)
        .from("topic_pages")
        .select("slug,title,tag,explainer,faqs,related_slugs,is_published,updated_at")
        .eq("slug", slug)
        .eq("is_published", true)
        .maybeSingle();
      if (error) throw error;
      return data ? mapRow(data) : null;
    },
  });

// ---- Admin ----

export const useAllTopicPages = (enabled: boolean) =>
  useQuery({
    queryKey: ["topic-pages", "admin"],
    enabled,
    queryFn: async (): Promise<TopicPage[]> => {
      const { data, error } = await (supabase as any)
        .from("topic_pages")
        .select("slug,title,tag,explainer,faqs,related_slugs,is_published,updated_at")
        .order("title");
      if (error) throw error;
      return (data || []).map(mapRow);
    },
  });

export const saveTopicPage = async (page: TopicPage): Promise<void> => {
  const { error } = await (supabase as any)
    .from("topic_pages")
    .update({
      title: page.title,
      explainer: page.explainer,
      faqs: page.faqs,
      is_published: page.is_published,
      updated_at: new Date().toISOString(),
    })
    .eq("slug", page.slug);
  if (error) throw error;
};

export const redraftTopicPage = async (slug: string): Promise<void> => {
  await draftTopicPage({ data: { slug } });
};
